import { Icons } from "@caspeco/casper-ui-library.components.icon";
import { IconButton } from "@caspeco/casper-ui-library.components.icon-button";
import { QuantityButton } from "@caspeco/casper-ui-library.components.quantity-button";
import { Stack } from "@caspeco/casper-ui-library.components.stack";
import { useEffect, useState } from "react";

/**
 * Gives an icon button if the quantity is 1. Gives quantity buttons if the quantity is greater than 1.
 * Have to ability to show inline errors if there is a minimum or maximum quantity that is exceeded.
 */
const AmountSelector = ({
    currentQuantity,
    totalGroupQuantities,
    setQuantity,
    addItemButtonText,
    minimumQuantity = Number.MIN_SAFE_INTEGER,
    maximumQuantity = Number.MAX_SAFE_INTEGER,
    minWarning = "Minimum quantity reached",
    maxWarning = "Maximum quantity reached",
    onErrorChange,
    disableAdd = false,
    blockAdd = false,
}: {
    currentQuantity: number;
    totalGroupQuantities: number;
    addItemButtonText: string;
    setQuantity: (quantity: number) => void;
    minimumQuantity?: number;
    maximumQuantity?: number;
    minWarning?: string;
    maxWarning?: string;
    onErrorChange: (isError: boolean, message: string) => void;
    disableAdd?: boolean;
    blockAdd?: boolean;
}) => {
    enum ErrorType {
        Minimum = "minimum",
        Maximum = "maximum",
        None = "none",
    }
    const [error, setError] = useState(ErrorType.None);

    const checkConstraints = (action: "add" | "remove"): boolean => {
        if (action === "add" && totalGroupQuantities >= maximumQuantity) {
            setError(ErrorType.Maximum);
            onErrorChange(true, maxWarning);
            return false;
        }

        if (action === "remove" && totalGroupQuantities <= minimumQuantity && currentQuantity > 0) {
            setError(ErrorType.Minimum);
            onErrorChange(true, minWarning);
            return false;
        }

        if (error !== ErrorType.None) {
            setError(ErrorType.None);
            onErrorChange(false, "");
        }
        return true;
    };

    // Check if we're no longer in an error state when totalGroupQuantities changes
    useEffect(() => {
        // Only run if we're in an error state
        if (error === ErrorType.None) return;

        if (error === ErrorType.Maximum && totalGroupQuantities < maximumQuantity) {
            setError(ErrorType.None);
            onErrorChange(false, "");
        }

        if (error === ErrorType.Minimum && totalGroupQuantities > minimumQuantity) {
            setError(ErrorType.None);
            onErrorChange(false, "");
        }
    }, [
        totalGroupQuantities,
        error,
        minimumQuantity,
        maximumQuantity,
        onErrorChange,
        ErrorType.None,
        ErrorType.Maximum,
        ErrorType.Minimum,
    ]);

    if (currentQuantity === 0) {
        return (
            <IconButton
                icon={Icons.Add}
                variant="tertiary"
                aria-label={addItemButtonText}
                isDisabled={disableAdd}
                onClick={() => {
                    checkConstraints("add");
                    setQuantity(currentQuantity + 1);
                }}
            />
        );
    } else {
        return (
            <Stack>
                <QuantityButton
                    quantity={currentQuantity}
                    onClickMinus={() => {
                        checkConstraints("remove");
                        setQuantity(currentQuantity - 1);
                    }}
                    onClickPlus={() => {
                        checkConstraints("add");
                        if (!blockAdd) setQuantity(currentQuantity + 1);
                    }}
                />
            </Stack>
        );
    }
};

export default AmountSelector;
